import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../organisms/Dialog';
import SelectFilter from '../../organisms/SelectFilter';
import { signatureApi } from '../../../services/signatureApi';
import { contractApi } from '../../../services/contractApi';

interface ProviderOption {
    providerId: string;
    name: string;
    displayName?: string;
    isActive: boolean;
}

interface SendToSignatureModalProps {
    contractId: string;
    operationNumber: string;
    fileName: string;
    onClose: () => void;
    onSuccess: (signatureProcessId: string) => void;
}

export default function SendToSignatureModal({ contractId, operationNumber, fileName, onClose, onSuccess }: SendToSignatureModalProps) {
    const [providers, setProviders] = useState<ProviderOption[]>([]);
    const [providerId, setProviderId] = useState('');
    const [sending, setSending] = useState(false);
    const [sendWhatsApp, setSendWhatsApp] = useState(false);
    const [sendSms, setSendSms] = useState(false);
    const [reminderDelay, setReminderDelay] = useState(3);
    const [reminderFrequency, setReminderFrequency] = useState(2);
    const [expireAfter, setExpireAfter] = useState(30);

    useEffect(() => {
        signatureApi.getProviders().then((res: any) => {
            const data = Array.isArray(res) ? res : res?.data || [];
            const active = data.filter((p: ProviderOption) => p.isActive);
            setProviders(active);
            if (active.length > 0) setProviderId(active[0].providerId);
        }).catch(() => toast.error('Erro ao carregar fornecedores'));
    }, []);

    const handleSend = async () => {
        if (!providerId) {
            toast.warn('Selecione um fornecedor');
            return;
        }
        setSending(true);
        try {
            const result = await contractApi.sendToSignature(contractId, providerId, { whatsapp: sendWhatsApp, sms: sendSms }, { reminderDelay, reminderFrequency, expireAfter });
            const processId = Array.isArray(result) ? result[0]?.signatureProcessId : result?.signatureProcessId || '';
            if (!processId) { toast.error('Processo criado mas ID não retornado'); return; }
            toast.success('Contrato enviado para assinatura!');
            onSuccess(processId);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao enviar para assinatura');
        } finally {
            setSending(false);
        }
    };

    const body = (
        <div className="space-y-5 p-2">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm space-y-2 text-gray-700 dark:text-gray-300">
                <p className="flex items-center gap-2"><span className="font-semibold text-blue-700 dark:text-blue-300">📋 Operação:</span> {operationNumber}</p>
                <p className="flex items-center gap-2"><span className="font-semibold text-blue-700 dark:text-blue-300">📄 Documento:</span> {fileName}</p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Selecione o Fornecedor de Assinatura</label>
                <SelectFilter
                    id="provider-select-modal"
                    label=""
                    value={providerId}
                    onChange={(value: string) => setProviderId(value)}
                    defaultVoidOption={true}
                    defaultLabel="Selecione um fornecedor..."
                    options={providers.map(p => ({ value: p.providerId, label: p.displayName || p.name }))}
                    className="w-full"
                />
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Canais de Notificação</label>
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked disabled className="rounded" />
                        📧 Email (obrigatório)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={sendWhatsApp} onChange={e => setSendWhatsApp(e.target.checked)}
                            className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" />
                        📱 WhatsApp (opcional)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)}
                            className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" />
                        💬 SMS (opcional)
                    </label>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Signatários sempre recebem por email. Canais adicionais requerem telefone cadastrado.</p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Lembretes e Expiração</label>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Primeiro lembrete (dias)</label>
                        <input type="number" min={1} max={30} value={reminderDelay} onChange={e => setReminderDelay(Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white" />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Frequência (dias)</label>
                        <input type="number" min={1} max={15} value={reminderFrequency} onChange={e => setReminderFrequency(Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white" />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Expirar após (dias)</label>
                        <input type="number" min={7} max={120} value={expireAfter} onChange={e => setExpireAfter(Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white" />
                    </div>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">DocuSign enviará lembretes automáticos para signatários pendentes.</p>
            </div>
        </div>
    );

    const footer = (
        <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                Cancelar
            </button>
            <button onClick={handleSend} disabled={sending || !providerId}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {sending ? 'Enviando...' : 'Enviar para Assinatura'}
            </button>
        </div>
    );

    return (
        <Dialog
            id="send-to-signature-modal"
            show={true}
            onClose={onClose}
            header="Enviar para Assinatura"
            body={body}
            footer={footer}
            size="md"
        />
    );
}
